import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/_old/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-base font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-neutral-100 disabled:border-disabled-500 disabled:text-disabled-500 duration-300",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 text-sm",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 text-sm",
        outline:
          "border border-neutral-200 bg-transparent hover:bg-secondary text-neutral-900 text-sm",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 text-sm",
        ghost: "hover:bg-accent hover:text-accent-foreground text-sm",
        link: "text-neutral-900 underline-offset-4 hover:underline py-3 px-4 max-h-[40px] text-sm",
        green:
          "text-neutral-900 bg-complementary-500 border border-neutral-900 py-3 px-4 max-h-[40px] disabled:bg-neutral-100 disabled:border-disabled-500 disabled:text-disabled-500 hover:bg-complementary-400",
        black:
          "bg-black border border-neutral-900 hover:bg-neutral-200 hover:text-neutral-900 text-white text-sm",
        primary:
          "bg-primary-500 hover:bg-primary-500/80 text-primary-500-foreground text-sm",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        xs: "rounded-md h-[24px] px-2",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        full: "w-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
